import React from "react";

// --- Smart pagination helper
// If 13 or fewer pages: show all pages
// If more than 13 pages: show first 3 + ... + current ±2 + ... + last 3
function calculatePagesToShow(currentPage, totalPages, delta = 2) {
  if (totalPages <= 1) return [1];

  // If 13 or fewer pages, show all pages
  if (totalPages <= 13) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  // For more than 13 pages, use smart pagination
  const pages = [];
  const firstPages = Math.min(3, totalPages);
  const lastStart = Math.max(totalPages - 2, firstPages + 1);

  // Current page range
  const currentStart = Math.max(1, currentPage - delta);
  const currentEnd = Math.min(totalPages, currentPage + delta);

  // Add first 3 pages
  for (let i = 1; i <= firstPages; i += 1) {
    pages.push(i);
  }

  // Add ellipsis and current range if needed
  if (currentStart > firstPages + 1) {
    pages.push("ellipsis-start");
    for (let i = currentStart; i <= currentEnd; i += 1) {
      pages.push(i);
    }
  } else {
    // Current range overlaps with first pages, just extend
    for (let i = firstPages + 1; i <= currentEnd; i += 1) {
      pages.push(i);
    }
  }

  // Add ellipsis and last 3 pages if needed
  if (currentEnd < lastStart - 1) {
    pages.push("ellipsis-end");
    for (let i = lastStart; i <= totalPages; i += 1) {
      pages.push(i);
    }
  } else {
    // Current range overlaps with last pages, just extend
    for (let i = Math.max(currentEnd + 1, lastStart); i <= totalPages; i += 1) {
      pages.push(i);
    }
  }

  // Remove duplicates while preserving order
  return [...new Set(pages)];
}

/**
 * Reusable pagination component for Bootstrap-styled pagination
 *
 * @param {number} currentPage - Current page number (1-indexed)
 * @param {number} totalPages - Total number of pages
 * @param {function} onPageChange - Callback when page changes: (newPage) => void
 * @param {boolean} loading - Whether data is currently loading
 * @param {number} total - Total number of results (for display)
 * @param {string} size - Bootstrap pagination size: "sm", "lg", or undefined for default
 */
function Pagination({ currentPage, totalPages, onPageChange, loading = false, total = null, size = "sm" }) {
  const pagesToShow = calculatePagesToShow(currentPage, totalPages);
  const sizeClass = size ? `pagination-${size}` : "";

  return (
    <div className="d-flex align-items-center gap-3 mt-4">
      <nav aria-label="Page navigation">
        <ul className={`pagination ${sizeClass} mb-0`}>
          <li className={`page-item ${currentPage <= 1 || loading ? "disabled" : ""}`}>
            <button
              type="button"
              className="page-link"
              href="#"
              tabIndex="-1"
              aria-disabled="true"
              aria-label="Previous"
              disabled={currentPage <= 1 || loading}
              onClick={() => {
                if (currentPage > 1 && !loading) {
                  onPageChange(Math.max(1, currentPage - 1));
                }
              }}
            >
              <span aria-hidden="true">&lsaquo;</span>
            </button>
          </li>
          {pagesToShow.map((pageNum) => {
            if (pageNum === "ellipsis-start" || pageNum === "ellipsis-end") {
              return (
                <li className="page-item disabled" key={pageNum}>
                  <span className="page-link">…</span>
                </li>
              );
            }

            return (
              <li className={`page-item ${currentPage === pageNum ? "active" : ""}`} key={pageNum}>
                {currentPage === pageNum ? (
                  <span className="page-link">{pageNum}</span>
                ) : (
                  <button type="button" className="page-link" onClick={() => onPageChange(pageNum)}>
                    {pageNum}
                  </button>
                )}
              </li>
            );
          })}
          <li className={`page-item ${currentPage >= totalPages || loading ? "disabled" : ""}`}>
            <button
              type="button"
              className="page-link"
              aria-label="Next"
              disabled={currentPage >= totalPages || loading}
              onClick={() => {
                if (currentPage < totalPages && !loading) {
                  onPageChange(Math.min(totalPages, currentPage + 1));
                }
              }}
            >
              <span aria-hidden="true">&rsaquo;</span>
            </button>
          </li>
        </ul>
      </nav>

      <span className="text-muted small text-nowrap">
        {loading && "Loading…"}
        {!loading && total !== null && `${total} results`}
      </span>
    </div>
  );
}

export default Pagination;
